// Google Analytics 4 utility for ContactSense
// Tracks: search queries, export events, job completion rates, conversion funnels

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

function isEnabled(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function' && !!GA_ID;
}

export function trackEvent(eventName: string, params: Record<string, unknown> = {}): void {
  if (!isEnabled()) return;
  window.gtag('event', eventName, params);
}

// ─── Search Events ────────────────────────────────────────────────────────────

/** Fired when a user submits an AI search query */
export function trackSearchQuery(query: string, resultCount?: number): void {
  trackEvent('search', {
    search_term: query,
    result_count: resultCount ?? 0,
    event_category: 'Search',
    event_label: 'AI Search Query',
  });
}

/** Fired when search results are returned */
export function trackSearchResults(query: string, contactCount: number, verifiedCount: number): void {
  trackEvent('search_results_loaded', {
    search_term: query,
    contact_count: contactCount,
    verified_count: verifiedCount,
    event_category: 'Search',
  });
}

// ─── Export Events ────────────────────────────────────────────────────────────

/** Fired when user exports contacts */
export function trackExport(format: 'csv' | 'xlsx' | 'json', contactCount: number, source: 'ai-search' | 'bulk-search' | 'contacts'): void {
  trackEvent('export', {
    export_format: format,
    contact_count: contactCount,
    export_source: source,
    event_category: 'Export',
    event_label: `Export ${format.toUpperCase()}`,
  });
}

// ─── Bulk Job Events ──────────────────────────────────────────────────────────

/** Fired when a bulk search job is started */
export function trackJobStarted(companyCount: number): void {
  trackEvent('bulk_job_started', {
    company_count: companyCount,
    event_category: 'Bulk Search',
    event_label: 'Job Started',
  });
}

/** Fired when a bulk search job completes */
export function trackJobCompleted(
  jobId: string,
  companyCount: number,
  contactsFound: number,
  verifiedCount: number,
  durationMs: number
): void {
  trackEvent('bulk_job_completed', {
    job_id: jobId,
    company_count: companyCount,
    contacts_found: contactsFound,
    verified_count: verifiedCount,
    duration_seconds: Math.round(durationMs / 1000),
    completion_rate: companyCount > 0 ? Math.round((contactsFound / companyCount) * 100) : 0,
    event_category: 'Bulk Search',
    event_label: 'Job Completed',
  });
}

/** Fired when a bulk search job is cancelled */
export function trackJobCancelled(jobId: string, progress: number): void {
  trackEvent('bulk_job_cancelled', {
    job_id: jobId,
    progress_at_cancel: progress,
    event_category: 'Bulk Search',
    event_label: 'Job Cancelled',
  });
}

// ─── Conversion Funnel Events ─────────────────────────────────────────────────

/** Fired when user signs up */
export function trackSignUp(method: string = 'email'): void {
  trackEvent('sign_up', {
    method,
    event_category: 'Conversion',
  });
}

/** Fired when user logs in */
export function trackLogin(method: string = 'email'): void {
  trackEvent('login', {
    method,
    event_category: 'Conversion',
  });
}

/** Fired when user views the AI search page (funnel entry) */
export function trackFunnelStep(step: string, stepNumber: number): void {
  trackEvent('funnel_step', {
    funnel_step_name: step,
    funnel_step_number: stepNumber,
    event_category: 'Conversion Funnel',
  });
}

/** Fired when user opens contact detail drawer */
export function trackContactView(company: string): void {
  trackEvent('contact_view', {
    company_name: company,
    event_category: 'Engagement',
  });
}
